import type { GameData } from './data.js';
import { HighNoon, TICK_RATE, type Input } from './model.js';
import { Renderer } from './render.js';
import { Audio } from './audio.js';
import { HighScores } from './highscores.js';

const $=<T extends HTMLElement=HTMLElement>(selector:string):T=>{
  const element=document.querySelector<T>(selector);if(!element)throw new Error(`Ontbrekend element: ${selector}`);return element;
};
const screen=$('#screen'),status=$('#game-status');
const audio=new Audio();
const scores=new HighScores();
audio.onInterruption=()=>{
  if(phase==='active'&&!game?.paused){pause(true);$('#pause-message').textContent='Geluid onderbroken? Tik op Verder spelen om spel en geluid te hervatten.';}
};
const held=new Map<string,Set<string>>(),latched=new Set<string>();
const gameKeys=['ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Space'];
let game:HighNoon|null=null,renderer:Renderer|null=null,canvas:HTMLCanvasElement|null=null;
let dataPromise:Promise<GameData>|null=null;
let phase:'idle'|'loading'|'active'|'error'='idle';
let frameId=0,lastTime=0,accumulator=0,lastStatus='',savedHighScore=0,generation=0;
let resumeAfterCheat=false;
let releaseFireBeforeStart=false;
scores.onOpen=()=>{releaseKeys();audio.setPaused(true);};
scores.onClose=()=>{releaseKeys();if(game?.mode==='game-over')game.step({});accumulator=0;lastTime=0;audio.setPaused(Boolean(game?.paused));canvas?.focus({preventScroll:true});};

function setStatus(text:string):void {if(text!==lastStatus){status.textContent=text;lastStatus=text;}}
function releaseKeys():void{held.clear();latched.clear();document.querySelectorAll('.is-held').forEach(button=>button.classList.remove('is-held'));}
function key(code:string,source:string,down:boolean):void{
  if(phase!=='active'||game?.paused||scores.blocking)return;
  const sources=held.get(code)||new Set<string>();
  if(down){if(!sources.size)latched.add(code);sources.add(source);}
  else sources.delete(source);
  if(sources.size)held.set(code,sources);else held.delete(code);
}
function readInput():Input{
  const on=(code:string)=>held.has(code)||latched.has(code);
  return{left:on('ArrowLeft'),right:on('ArrowRight'),up:on('ArrowUp'),down:on('ArrowDown'),fire:on('Space')};
}

async function assets():Promise<GameData>{
  if(!dataPromise)dataPromise=(async()=>{
    const response=await fetch(new URL('../assets/game-data.json',import.meta.url),{signal:AbortSignal.timeout(8000)});
    if(!response.ok)throw new Error(`Speldata niet gevonden (HTTP ${response.status}).`);
    const data=await response.json() as GameData;
    if(data.source?.format!=='decoded-data-only-v1'||data.font?.length!==2048||!data.scenes?.town||!data.rules?.move||!data.music?.tracks?.gameplay?.notes?.length)throw new Error('De speldata is onvolledig. Pak de nieuwste versie opnieuw uit.');
    return data;
  })().catch(error=>{dataPromise=null;throw error;});
  return dataPromise;
}

function updateStatus():void{
  if(!game)return;
  if(scores.blocking){setStatus($('#score-form').hidden?'Score wordt gecontroleerd…':'Highscore! Vul je naam in of kies Niet opslaan.');return;}
  if(game.paused){setStatus('Even pauze');return;}
  const labels={title:'Kies je level. Spatie om te beginnen.',playing:`Golf ${game.wave} · Level ${game.level} · ${game.lives} levens`,'wave-clear':'Golf voltooid. Tijd voor het bonusduel.','duel-wait':'Laat vuur los en wacht op het signaal.',duel:'TREK! Spatie om te schieten.','game-over':'Game over. Spatie voor het titelscherm.'};
  setStatus(labels[game.mode]);
}

function loop(now:number):void{
  if(phase!=='active'||!game||!renderer)return;
  const elapsed=lastTime?Math.min(now-lastTime,250):0;lastTime=now;
  if(!game.paused){
    accumulator+=elapsed;
    while(accumulator>=1000/TICK_RATE&&!scores.blocking){
      const input=readInput(),before=game.mode;
      if(releaseFireBeforeStart){if(!input.fire)releaseFireBeforeStart=false;input.fire=false;}
      game.step(input);scores.record(input,before,game);
      if(before==='game-over'&&game.mode==='title'){
        const highScore=game.highScore;
        game=new HighNoon(game.data,scores.reset(true));game.highScore=highScore;
        releaseFireBeforeStart=true;
      }
      latched.clear();accumulator-=1000/TICK_RATE;
      audio.syncMusic(game);
      for(const sound of game.sounds)audio.play(sound);
    }
    if(scores.blocking)accumulator=0;
    renderer.render(game);updateStatus();
    if(game.highScore>savedHighScore){
      savedHighScore=game.highScore;
      try{localStorage.setItem('highnoon.native.highscore',String(savedHighScore));}catch{}
    }
  }else accumulator=0;
  frameId=requestAnimationFrame(loop);
}

function fail(message:string):void{
  phase='error';releaseKeys();cancelAnimationFrame(frameId);audio.stopMusic();
  $('#loading').hidden=true;$('#paused').hidden=true;$('#error').hidden=false;
  $('#error-message').textContent=message;
  $<HTMLButtonElement>('#pause').disabled=true;$<HTMLButtonElement>('#restart').disabled=false;
  $<HTMLFieldSetElement>('#cheat-controls').disabled=true;
  canvas?.remove();canvas=null;renderer=null;game=null;
  setStatus('Het starten is niet gelukt');$('#retry').focus();
}

async function start():Promise<void>{
  scores.reset();
  resumeAfterCheat=false;
  releaseFireBeforeStart=false;
  const current=++generation;releaseKeys();cancelAnimationFrame(frameId);canvas?.remove();audio.stopMusic();
  canvas=null;game=null;renderer=null;phase='loading';
  $('#poster').hidden=true;$('#error').hidden=true;$('#paused').hidden=true;$('#loading').hidden=false;
  $('#loading-text').textContent='De originele graphics en vertaalde spelregels worden klaargezet.';
  $<HTMLButtonElement>('#pause').disabled=true;$<HTMLButtonElement>('#restart').disabled=true;
  $<HTMLFieldSetElement>('#cheat-controls').disabled=true;$('#cheat-message').textContent='';
  $<HTMLFormElement>('#cheat-form').reset();
  $('#pause-icon').textContent='Ⅱ';setStatus('De speldata wordt geladen…');
  if(location.protocol==='file:'){fail('Open dit spel via een webserver: voer npm run dev uit in high-noon-web en open http://localhost:5173.');return;}
  void audio.unlock(); // Create/resume in the user's click, before fetching.
  try{
    const data=await assets();if(current!==generation)return;
    canvas=document.createElement('canvas');canvas.className='native-game';canvas.tabIndex=0;
    canvas.setAttribute('aria-label','High Noon. Pijltjestoetsen om te bewegen en een level te kiezen. Spatie om te beginnen en te schieten.');
    screen.append(canvas);renderer=new Renderer(canvas,data);game=new HighNoon(data,scores.reset());
    audio.loadMusic(data.music);audio.syncMusic(game);
    try{savedHighScore=Math.max(0,Number(localStorage.getItem('highnoon.native.highscore'))||0);}catch{savedHighScore=0;}
    game.highScore=savedHighScore;phase='active';$('#loading').hidden=true;
    $<HTMLButtonElement>('#pause').disabled=false;$<HTMLButtonElement>('#restart').disabled=false;
    $<HTMLFieldSetElement>('#cheat-controls').disabled=false;
    renderer.render(game);lastTime=0;accumulator=0;updateStatus();canvas.focus();
    void audio.unlock();frameId=requestAnimationFrame(loop);
  }catch(error){
    if(current!==generation)return;
    fail(error instanceof Error ? error.name==='TimeoutError'?'Het ophalen van de speldata duurt te lang. Controleer of de lokale server nog draait.':error.message:'Het spel kon niet worden gestart.');
  }
}

function pause(paused:boolean,moveFocus=true):void{
  if(phase!=='active'||!game||scores.blocking)return;
  resumeAfterCheat=false;
  releaseKeys();game.paused=paused;audio.setPaused(paused);accumulator=0;lastTime=0;
  $('#paused').hidden=!paused;$('#pause').setAttribute('aria-label',paused?'Verder spelen':'Pauzeren');
  $('#pause-icon').textContent=paused?'▷':'Ⅱ';
  $('#pause-message').textContent='Tik op Verder spelen om door te gaan.';
  if(paused){if(moveFocus)$('#resume').focus();}else{void audio.unlock();canvas?.focus();}
  updateStatus();
}

function toggleSound():void{
  audio.setEnabled(!audio.enabled);$('#sound').setAttribute('aria-pressed',String(audio.enabled));
  $('#sound').setAttribute('aria-label',audio.enabled?'Geluid uitschakelen':'Geluid inschakelen');
  $('#sound-label').textContent=audio.enabled?'GELUID AAN':'GELUID UIT';if(audio.enabled)void audio.unlock();
  if(phase==='active'&&!game?.paused&&!scores.blocking)canvas?.focus({preventScroll:true});
}

$('#start').addEventListener('click',()=>void start());
$('#retry').addEventListener('click',()=>void start());
$('#restart').addEventListener('click',()=>void start());
$('#pause').addEventListener('click',()=>pause(!game?.paused));
$('#resume').addEventListener('click',()=>pause(false));
$('#sound').addEventListener('click',toggleSound);
$('#cheat-code').addEventListener('focus',()=>{
  releaseKeys();
  if(game&&!game.paused){pause(true,false);resumeAfterCheat=true;}
});
$('#cheat-form').addEventListener('submit',event=>{
  event.preventDefault();
  const field=$<HTMLInputElement>('#cheat-code');
  const result=game?.applyCheat(field.value)??'inactive';
  const messages={potion:`Vijf extra levens! Je hebt nu ${game?.lives} levens.`,blanks:'Blanks actief: vijandelijke kogels kunnen de sheriff niet raken.',bullets:'Echte kogels actief: vijandelijke kogels kunnen de sheriff weer doden.',inactive:'Begin eerst een spel met spatie of VUUR en voer daarna je code in.',unknown:'Onbekende cheatcode. Probeer opnieuw.'};
  $('#cheat-message').textContent=messages[result];
  if(result==='potion'||result==='blanks'||result==='bullets'){
    scores.useCheat(result);
    field.value='';renderer?.render(game!);
    if(resumeAfterCheat)pause(false);
    updateStatus();
  }
});
$('#fullscreen').addEventListener('click',async()=>{
  try{if(document.fullscreenElement)await document.exitFullscreen();else await $('#screen-shell').requestFullscreen();}
  catch{setStatus('Volledig scherm is hier niet beschikbaar');}
});

for(const button of document.querySelectorAll<HTMLButtonElement>('[data-keys]')){
  const keys=button.dataset.keys!.split(' ');
  button.addEventListener('pointerdown',event=>{
    if(phase!=='active'||game?.paused||scores.blocking)return;
    event.preventDefault();button.setPointerCapture(event.pointerId);void audio.unlock();
    for(const code of keys)key(code,`pointer-${event.pointerId}-${button.dataset.keys}`,true);
    button.classList.add('is-held');
  });
  const release=(event:PointerEvent)=>{
    for(const code of keys)key(code,`pointer-${event.pointerId}-${button.dataset.keys}`,false);
    button.classList.remove('is-held');
  };
  button.addEventListener('pointerup',release);button.addEventListener('pointercancel',release);button.addEventListener('lostpointercapture',release);
  button.addEventListener('contextmenu',event=>event.preventDefault());
}

window.addEventListener('keydown',event=>{
  if(scores.blocking)return;
  if(event.altKey||event.ctrlKey||event.metaKey||/INPUT|TEXTAREA|SELECT/.test((event.target as HTMLElement).tagName))return;
  if(event.code==='Space'&&(event.target as HTMLElement).closest('button,a'))return;
  if(gameKeys.includes(event.code)&&phase==='active'){
    event.preventDefault();if(!event.repeat){void audio.unlock();key(event.code,'keyboard',true);}
  }else if(event.code==='KeyP'&&!event.repeat)pause(!game?.paused);
  else if(event.code==='KeyM'&&!event.repeat)toggleSound();
});
window.addEventListener('keyup',event=>{if(gameKeys.includes(event.code))key(event.code,'keyboard',false);});
function background():void{resumeAfterCheat=false;releaseKeys();if(phase==='active'&&!game?.paused)pause(true);}
window.addEventListener('blur',background);
window.addEventListener('pagehide',background);
function recoverAudio():void{if(phase==='active'&&!game?.paused&&!document.hidden)void audio.unlock();}
window.addEventListener('focus',recoverAudio);
window.addEventListener('pageshow',recoverAudio);
document.addEventListener('visibilitychange',()=>{if(document.hidden)background();else recoverAudio();});
document.addEventListener('fullscreenchange',()=>{
  $('#fullscreen').setAttribute('aria-label',document.fullscreenElement?'Volledig scherm verlaten':'Volledig scherm');
  if(phase==='active'&&!game?.paused&&!scores.blocking)canvas?.focus();
});

// Keep the display in view when a phone switches between the two control layouts.
const landscape=window.matchMedia?.('(orientation:landscape) and (max-height:600px) and (pointer:coarse)');
landscape?.addEventListener('change',()=>{
  releaseKeys();
  requestAnimationFrame(()=>$('#screen-shell').scrollIntoView({block:'start',behavior:'instant'}));
});

// Read-only inspection for browser QA. Native values, no emulated addresses.
declare global { interface Window { highNoonState:()=>unknown; } }
window.highNoonState=()=>({native:true,ready:phase==='active',mode:game?.mode,level:game?.level,wave:game?.wave,score:game?.score,lives:game?.lives,blanks:game?.blanks,paused:game?.paused,muted:!audio.enabled,music:audio.musicState,player:game?.player?{x:game.player.x,y:game.player.y,brain:game.player.brain}:null,bullets:game?.bullets.length,entities:game?.entities.length});
